// English application dictionary.
// This file is the SOURCE OF TRUTH for translation keys — its shape derives the
// TranslationKey type used by t() everywhere else, so cs.ts and sk.ts must
// match its structure exactly.

const en = {
    app: {
        brand: 'Faust Signing Service',
        suite: 'Wolters Kluwer',
    },
    locale: {
        switch: 'Language',
        cs: 'Čeština',
        sk: 'Slovenčina',
        en: 'English',
    },
    nav: {
        overview: 'Overview',
        publications: 'Publications',
        files: 'Files',
        queue: 'Queue',
        users: 'Users',
        processing: 'Processing',
        management: 'Management',
        monitoring: 'Monitoring',
        oauthApplications: 'OAuth Applications',
    },
    menu: {
        overview: 'Overview',
        publications: 'Publications',
        files: 'Files',
        processing: 'Processing',
        pdfWatermarkDb: 'PDF Watermark (DB)',
        epubSignDb: 'EPUB Sign (DB)',
        monitoring: 'Monitoring',
        users: 'Users',
        invitations: 'Invitations',
        oauthApplications: 'OAuth Applications',
        applicationManagement: 'Application Management',
        catalogSync: 'Catalog Synchronization',
        massUpload: 'Document Upload (Mass)',
        verifyPipeline: 'Verify Pipeline',
        fingerprintCheck: 'Fingerprint Check',
    },
    dashboard: {
        heroTitle: 'A cleaner operating surface for publications, queueing, and verification.',
        heroSubtitle:
            'Manage publications, monitor the processing queue, and verify signing results from a single console.',
        actionOpenQueue: 'Open queue',
        actionBrowsePublications: 'Browse publications',
        cards: {
            publications: {
                title: 'Publications',
                description:
                    'Manage titles and entry points that feed downstream processing and verification flows.',
                action: 'Open publications',
            },
            queue: {
                title: 'Queue',
                description: 'Review processing progress, errors, and recent jobs in one operational view.',
                action: 'Review queue',
            },
            files: {
                title: 'Files',
                description:
                    'Inspect stored files, exports, and source assets before they reach processing.',
                action: 'Open files',
            },
            bulkUpload: {
                title: 'Bulk upload',
                description: 'Prepare and submit mass document uploads for PDF and EPUB titles.',
                action: 'Start upload',
            },
            verification: {
                title: 'Verification',
                description:
                    'Check the end-to-end signing pipeline before publishing results to users.',
                action: 'Run verification',
            },
            fingerprint: {
                title: 'Fingerprint',
                description:
                    'Confirm document identity and compare extracted fingerprints against known records.',
                action: 'Inspect fingerprints',
            },
            users: {
                title: 'Users',
                description:
                    'Keep operators, invitations, and OAuth applications in a predictable admin surface.',
                action: 'Manage users',
            },
            operations: {
                title: 'Operations',
                description: 'Run catalog synchronization and other operational tools from one place.',
                action: 'Open operations',
            },
        },
    },
    auth: {
        login: {
            subtitle: 'Sign in to continue to the console',
            email: 'Email',
            password: 'Password',
            submit: 'Sign in',
            submitting: 'Signing in…',
            invalidCredentials: 'Invalid email or password',
        },
        changePassword: {
            menuItem: 'Change Password',
            titleSelf: 'Change Your Password',
            titleAdmin: 'Change Password for %{name}',
            adminNotice: 'You are changing the password for user: %{name}',
            fields: {
                oldPassword: 'Old Password',
                newPassword: 'New Password',
                confirmPassword: 'Confirm Password',
            },
            requirementsTitle: 'Password requirements:',
            requirements: {
                length: 'At least 8 characters',
                uppercase: 'At least 1 uppercase letter',
                lowercase: 'At least 1 lowercase letter',
                digit: 'At least 1 digit',
            },
            actions: {
                cancel: 'Cancel',
                submit: 'Change Password',
                submitting: 'Changing...',
            },
            notifications: {
                authRequired: 'Authentication required',
                success: 'Password changed successfully',
                relogin: 'Please login again with your new password',
                failed: 'Failed to change password',
            },
            errors: {
                oldPasswordRequired: 'Old password is required',
                newPasswordRequired: 'New password is required',
                tooShort: 'Password must be at least 8 characters',
                missingUppercase: 'Must contain uppercase letter',
                missingLowercase: 'Must contain lowercase letter',
                missingDigit: 'Must contain digit',
                confirmRequired: 'Password confirmation is required',
                mismatch: 'Passwords do not match',
            },
        },
        defaultUser: 'User',
    },
    actions: {
        exportCsv: 'Export CSV',
        copyCode: 'Copy Code',
        done: 'Done',
        copy: 'Copy',
        download: 'Download',
        refresh: 'Refresh',
        apply: 'Apply',
        clear: 'Clear',
        all: 'All',
    },
    notifications: {
        authRequired: 'Authentication required',
        copiedToClipboard: 'Copied to clipboard',
        downloadSuccess: 'File downloaded successfully',
        downloadFailed: 'Failed to download file',
    },
    permissions: {
        noAccess: 'You do not have permission to view this section.',
    },
    resources: {
        users: {
            name: 'User |||| Users',
            fields: {
                id: 'ID',
                email: 'Email',
                full_name: 'Full name',
                is_active: 'Active',
                is_admin: 'Admin',
                created_at: 'Created at',
                updated_at: 'Updated at',
                password: 'Password',
                invite_code: 'Invite code',
                role: 'Role',
            },
            filters: {
                emailPlaceholder: 'Filter by email',
            },
            empty: {
                title: 'No users found',
                hint: 'Create a new user or invite someone to join.',
            },
            actions: {
                changePassword: 'Change Password',
            },
            helpers: {
                passwordKeepCurrent: 'Leave empty to keep current password',
                inviteCode: 'The invitation code for this user',
            },
            roles: {
                admin: 'Admin',
                user: 'User',
            },
            notifications: {
                exportSuccess: 'Users exported successfully',
                exportFailed: 'Failed to export users',
                fetchFailed: 'Failed to fetch users',
            },
        },
        invitations: {
            name: 'Invitation |||| Invitations',
            fields: {
                id: 'ID',
                email: 'Email',
                code: 'Code',
                is_active: 'Active',
                is_admin: 'Admin privileges',
                invited_by: 'Invited by',
                created_at: 'Created at',
                expires_at: 'Expires at',
            },
            filters: {
                emailPlaceholder: 'Filter by email',
            },
            empty: {
                title: 'No invitations found',
                hint: 'Create a new invitation using the "Create" button above.',
            },
            actions: {
                createInvitation: 'Create invitation',
            },
            notifications: {
                codeCopied: 'Invitation code copied to clipboard',
                codeCopyFailed: 'Failed to copy invitation code',
                exportSuccess: 'Invitations exported successfully',
                exportFailed: 'Failed to export invitations',
                fetchFailed: 'Failed to fetch invitations',
            },
            permissionDenied: {
                view: 'You do not have permission to view invitations.',
                viewDetails: 'You do not have permission to view invitation details.',
                create: 'You do not have permission to create invitations.',
            },
        },
        'oauth-applications': {
            name: 'OAuth Application |||| OAuth Applications',
            fields: {
                name: 'Application Name',
                description: 'Description',
                client_id: 'Client ID',
                client_secret: 'Client Secret',
                scopes: 'Scopes',
                is_active: 'Active',
                created_at: 'Created',
            },
            permissionDenied: {
                manage: 'Only administrators can manage OAuth applications.',
                view: 'Only administrators can view OAuth applications.',
            },
            helpers: {
                scopesCreate: 'Select permission scopes for the application (use Ctrl/Cmd to select multiple)',
                scopesEdit: 'Use Ctrl/Cmd to select multiple',
            },
            notifications: {
                created: 'OAuth application created successfully',
            },
            secretDialog: {
                title: '⚠️ Save Your OAuth Credentials',
                alertBoldLabel: 'Important:',
                alertText:
                    'This is the ONLY time you will see the client secret. Save it securely now — you cannot retrieve it again!',
            },
        },
        publications: {
            name: 'Publication |||| Publications',
            fields: {
                id: 'ID',
                sku: 'SKU',
                title: 'Title',
                description: 'Description',
                created_at: 'Created at',
                updated_at: 'Updated at',
            },
            filters: {
                skuPlaceholder: 'Filter by SKU',
                titlePlaceholder: 'Filter by title',
            },
            empty: {
                title: 'No publications found',
                hint: 'Create a new publication using the "Create" button above.',
            },
            helpers: {
                skuUnique: 'Unique publication identifier',
            },
            relatedFilesLabel: 'Files',
            notifications: {
                exportSuccess: 'Publications exported successfully',
                exportFailed: 'Failed to export publications',
                fetchFailed: 'Failed to fetch publications',
            },
        },
        files: {
            name: 'File |||| Files',
            fields: {
                id: 'ID',
                publication_id: 'Publication',
                file_type: 'Type',
                filename: 'Filename',
                mime_type: 'MIME Type',
                size: 'Size',
                uploaded_by: 'Uploaded by',
                created_at: 'Created at',
                updated_at: 'Updated at',
            },
            filters: {
                skuPlaceholder: 'Filter by SKU',
                filenamePlaceholder: 'Filter by filename',
            },
            empty: {
                title: 'No files found',
                hint: 'Upload a file using the "Create" button above.',
            },
            create: {
                title: 'Upload File',
                saveLabel: 'Upload file',
                savingLabel: 'Uploading…',
                loadingPublications: 'Loading publications…',
                selectPublicationPlaceholder: 'Select a publication',
            },
            form: {
                publication: 'Publication',
                exactSku: 'Exact SKU',
                fileType: 'File Type',
                file: 'File',
                selectFileButton: 'Select file',
            },
            helpers: {
                publication: 'Pick a publication by SKU or title, or enter an exact SKU below.',
                exactSku: 'Optional. If filled, this exact SKU is used instead of the selected publication.',
                file: 'Supported file types: PDF, EPUB',
            },
            fileTypes: {
                pdf: 'PDF',
                epub: 'EPUB',
            },
            notifications: {
                publicationsLoadFailed: 'Failed to load publications',
                exportSuccess: 'Files exported successfully',
                exportFailed: 'Failed to export files',
                uploadSuccess: 'File uploaded successfully',
                uploadFailedPrefix: 'Upload failed',
                selectFile: 'Please select a file to upload',
                selectPublicationOrSku: 'Select a publication or enter an exact SKU',
                selectFileType: 'Please select a file type',
            },
        },
        queue: {
            name: 'Queue item |||| Queue items',
            fields: {
                queue_id: 'Queue ID',
                sku: 'SKU',
                processing_type: 'Type',
                status: 'Status',
                progress: 'Progress',
                consumer_name: 'Name',
                consumer_surname: 'Surname',
                consumer_address: 'Address',
                consumer_telephone: 'Telephone',
                consumer_email: 'Email',
                error_code: 'Error code',
                error_message: 'Error message',
                backchannel_url: 'Backchannel URL',
                created_at: 'Created at',
                updated_at: 'Updated at',
            },
            empty: {
                title: 'No queue items found',
                hint: 'Submit documents for processing using the "Create" button above.',
            },
            create: {
                title: 'Sign Document',
                saveLabel: 'Submit for signing',
            },
            sections: {
                document: 'Document Information',
                consumer: 'Consumer Information',
                optional: 'Optional Settings',
            },
            form: {
                processingType: 'Processing Type',
                publicationSku: 'Publication SKU',
                firstName: 'First Name',
                lastName: 'Last Name',
                address: 'Address',
                telephone: 'Telephone',
                email: 'Email',
                backchannelUrl: 'Backchannel URL',
            },
            helpers: {
                backchannelUrl: 'Optional callback URL for notifications when processing completes',
            },
            processingTypes: {
                watermark: 'PDF Watermark',
                sign: 'EPUB Sign',
            },
            statuses: {
                pending: 'Pending',
                processing: 'Processing',
                completed: 'Completed',
                error: 'Error',
                unknown: 'Unknown',
            },
            actions: {
                refresh: 'Refresh',
                download: 'Download',
            },
            notifications: {
                statusRefreshed: 'Status refreshed',
                downloadSuccess: 'File downloaded successfully',
                downloadFailed: 'Failed to download file',
                signFailedPrefix: 'Failed to sign',
                queuedSuccess: 'Document queued successfully. Queue ID: %{queueId}',
            },
            summaryText: 'The document will be processed asynchronously. You can check the status in the queue list.',
        },
    },
    monitoring: {
        title: 'Monitoring',
        stats: {
            total: 'Total',
            today: 'Today',
            thisWeek: 'This week',
            thisMonth: 'This month',
            doneToday: 'Done today',
            errorsToday: 'Errors today',
        },        charts: {
            daily: 'Daily requests (last 30 days)',
            dailyTotal: 'Total',
            dailyCompleted: 'Completed',
            dailyErrors: 'Errors',
            statusDistribution: 'Status distribution',
        },
        filters: {
            title: 'Filters',
            sku: 'SKU',
            consumerEmail: 'Consumer email',
            status: 'Status',
            processingType: 'Processing type',
            documentType: 'Document type',
        },
        processingTypes: {
            watermark: 'Watermark',
            sign: 'Sign',
        },
        documentTypes: {
            pdf: 'PDF',
            epub: 'EPUB',
        },
        table: {
            queueId: 'Queue ID',
            operator: 'Operator',
            sku: 'SKU',
            consumerEmail: 'Consumer email',
            type: 'Type',
            doc: 'Doc',
            status: 'Status',
            progress: 'Progress',
            created: 'Created',
            completed: 'Completed',
            empty: 'No records found',
        },
        detail: {
            title: 'Request detail',
            queueId: 'Queue ID',
            status: 'Status',
            progress: 'Progress',
            operator: 'Operator',
            sku: 'SKU',
            consumerEmail: 'Consumer email',
            processingType: 'Processing type',
            documentType: 'Document type',
            createdAt: 'Created at',
            startedAt: 'Started at',
            completedAt: 'Completed at',
            errorCode: 'Error code',
            errorMessage: 'Error message',
            downloadProcessed: 'Download processed document',
        },
        notifications: {
            loadFailed: 'Failed to load monitoring data',
            sseInterrupted: 'Realtime monitoring connection interrupted',
            downloadSuccess: 'Downloaded successfully',
            downloadFailed: 'Download failed',
        },
        tooltips: {
            refresh: 'Refresh',
            download: 'Download',
        },
    },
    processing: {
        pdf: {
            title: 'PDF Watermark (Database File)',
            heading: 'Watermark PDF from Database',
            description: 'Select a publication and provide consumer information to queue PDF watermark processing.',
            actionLabel: 'Watermark PDF',
            actionLoadingLabel: 'Queueing…',
            publicationHelper: 'Search only publications that already have a PDF file available for processing.',
            notifications: {
                queuedSuccess: 'PDF queued successfully. Queue ID: %{queueId}',
                failedPrefix: 'Failed to queue PDF watermark',
            },
        },
        epub: {
            title: 'EPUB Sign (Database File)',
            heading: 'Sign EPUB from Database',
            description: 'Select a publication and provide consumer information to queue EPUB signing.',
            actionLabel: 'Sign EPUB',
            actionLoadingLabel: 'Queueing…',
            publicationHelper: 'Search only publications that already have an EPUB file available for processing.',
            notifications: {
                queuedSuccess: 'EPUB queued successfully. Queue ID: %{queueId}',
                failedPrefix: 'Failed to queue EPUB signing',
            },
        },
        publicationLabel: 'Publication',
        consumerSection: 'Consumer Information',
        asyncNote: 'The document will be processed asynchronously through the queue. Track progress in Monitoring.',
        notifications: {
            selectPublication: 'Select a publication',
            publicationsLoadFailed: 'Failed to load publications',
        },
    },
    catalog: {
        title: 'Catalog Synchronization',
        permissionDenied: 'Access restricted to administrators.',
        sync: {
            heading: 'Catalog Synchronization',
            description: 'Downloads the catalog from Wolters Kluwer and synchronizes publications in the database.',
            actionLabel: 'Synchronize catalog',
            actionRunningLabel: 'Synchronization in progress…',
            resultsTitle: 'Synchronization results',
            progressTitle: 'Synchronization progress',
            completed: 'Completed',
            stats: {
                total: 'Total: %{value}',
                created: 'Created: %{value}',
                updated: 'Updated: %{value}',
                errors: 'Errors: %{value}',
            },
            events: {
                tokenMissing: 'Authentication token missing. Please log in again.',
                sseInterrupted: 'SSE connection interrupted.',
            },
        },
        danger: {
            sectionTitle: 'Danger Zone',
            cannotBeUndone: '⚠️ This action cannot be undone',
            confirmPromptSuffix: 'to confirm:',
            confirmInputLabel: 'Type "%{word}" to confirm',
            confirmInputError: 'Must be exactly "%{word}"',
            cancel: 'Cancel',
            deleting: 'Deleting…',
            confirmDelete: 'Confirm delete',
            allFiles: {
                title: 'Delete ALL files',
                description: 'Permanently deletes every document file from the database. Publications remain in the catalog but will have no associated files. Processing-queue entries are preserved (source_file references are cleared).',
                buttonLabel: 'Delete all files',
            },
            allPublications: {
                title: 'Delete entire catalog',
                description: 'Permanently deletes ALL publications together with all their files, document fingerprints, and fingerprint verifications. Processing-queue and signed-document audit records are preserved.',
                buttonLabel: 'Delete catalog',
            },
        },
    },
    massUpload: {
        title: 'Document Upload (Mass)',
        permissionDenied: 'Access restricted to administrators.',
        heading: 'Document Upload (Mass)',
        descriptionPrefix: 'Upload multiple PDF or EPUB files at once. Filenames must follow the pattern',
        descriptionOr: 'or',
        descriptionSuffix: '. Files for unknown SKUs are skipped. Existing files are replaced.',
        dropZone: {
            filesSelected: '%{count} file(s) selected',
            placeholder: 'Click or drag & drop files here',
            accepted: 'Accepted: .pdf, .epub',
        },
        actions: {
            upload: 'Upload',
            uploadCount: 'Upload %{count} file(s)',
            uploadingProgress: 'Uploading… (%{progress}%)',
        },
        resultsTitle: 'Upload results',
        progressTitle: 'Upload progress',
        completed: 'Completed',
        stats: {
            total: 'Total: %{value}',
            created: 'Created: %{value}',
            updated: 'Updated: %{value}',
            skipped: 'Skipped: %{value}',
            errors: 'Errors: %{value}',
        },
        events: {
            tokenMissing: 'Authentication token missing. Please log in again.',
            starting: 'Starting upload of %{count} file(s)…',
            unknownResponse: 'Unknown response',
            networkError: '\'%{filename}\': network error — %{message}',
            summary: 'Upload complete — Created: %{created}, Updated: %{updated}, Skipped: %{skipped}, Errors: %{errors}, Total: %{total}',
        },
    },
    verifyPage: {
        title: 'Verify Pipeline',
        permissionDenied: 'Access restricted to administrators.',
        heading: 'Verify Pipeline',
        description: 'End-to-end test: Watermark → Fingerprint → Verify → Extract → Tampering check. Pick a file from the database or use the test files.',
        fileSection: {
            title: 'File to verify',
            selectLabel: 'Select a file',
            loading: 'Loading files…',
            placeholder: '— Select a file —',
        },
        consumerSection: {
            title: 'Consumer data',
            name: 'First name',
            surname: 'Last name',
            email: 'Email',
            address: 'Address',
            phone: 'Phone',
        },
        actions: {
            start: 'Start verification',
            running: 'Pipeline is running…',
        },
        progress: {
            stepLabel: 'Step %{current} / %{total}',
        },
        result: {
            title: 'Verification result',
            verified: 'VERIFIED ✓',
            notVerified: 'NOT VERIFIED ✗',
            confidence: 'Confidence: %{value}%',
            bits: 'Bits: %{value}',
            type: 'Type: %{value}',
            original: 'Original: %{value} KB',
            final: 'Final: %{value} KB',
        },
        log: {
            title: 'Pipeline Log',
            completedVerified: 'Completed ✓',
            completed: 'Completed',
            stepFormat: '── Step %{step}: %{title} ──',
        },
        errors: {
            tokenMissing: 'Token missing. Please log in again.',
            sseInterrupted: 'SSE connection interrupted.',
        },
    },
    fingerprintVerify: {
        title: 'Fingerprint verification',
        permissionDenied: 'Access restricted to administrators.',
        heading: 'Document fingerprint verification',
        description: 'Upload a PDF or EPUB file and verify whether it contains a digital fingerprint. You can provide data manually or match against the database.',
        steps: {
            upload: '1. Upload file',
            mode: '2. Verification method',
        },
        upload: {
            select: 'Select file',
        },
        mode: {
            manual: 'Manual verification — enter Queue ID and/or email',
            dbAll: 'Compare against all fingerprints in the database',
            dbSpecific: 'Compare against a specific fingerprint from the database',
        },
        manual: {
            queueIdLabel: 'Queue ID (optional)',
            emailLabel: 'Customer email (optional)',
            queueIdPlaceholder: '550e8400-e29b-41d4-a716-...',
            emailPlaceholder: 'john.doe@example.com',
        },
        dbSpecific: {
            selectLabel: 'Select a fingerprint from the database',
            loading: 'Loading…',
            empty: 'No fingerprints in the database',
            placeholder: '— Select a fingerprint —',
        },
        dbAll: {
            info: 'The document will be compared against all %{count} fingerprints in the database.',
        },
        actions: {
            submit: 'Verify fingerprint',
            submitting: 'Verification in progress…',
        },
        errors: {
            unexpected: 'Unexpected error',
        },
        extraction: {
            title: 'Fingerprint extraction',
            foundAlert: 'Fingerprint found in the document',
            notFoundAlert: 'No fingerprint was found in the document',
            notFoundSuffix: ' — %{reason}',
            fields: {
                method: 'Method',
                queueId: 'Queue ID',
                email: 'Customer email',
                name: 'Name',
                sku: 'SKU',
                bitsExtracted: 'Bits extracted',
            },
        },
        verifyResult: {
            title: 'Verification result',
            verified: 'VERIFIED ✓',
            notVerified: 'NOT VERIFIED ✗',
            confidence: 'Confidence: %{value}%',
            metaOk: 'Metadata ✓',
            metaFail: 'Metadata ✗',
            contentOk: 'Content ✓',
            contentFail: 'Content ✗',
        },
        tamper: {
            title: 'Integrity check (tampering)',
            detected: 'The document was probably altered after the fingerprint was applied!',
            notDetected: 'The document shows no signs of tampering',
        },
        dbMatch: {
            title: 'Database comparison',
            noFingerprint: 'No fingerprint was found in the document — cannot compare against the database.',
            noMatches: 'Fingerprint found but no matches in the database (0 records).',
            matchesFound: 'Found %{count} match(es) in the database',
            fields: {
                id: 'ID',
                email: 'Email',
                name: 'Name',
                sku: 'SKU',
                type: 'Type',
                queueId: 'Queue ID',
                match: 'Match',
            },
            tags: {
                qidOk: 'QID ✓',
                qidFail: 'QID ✗',
                emailOk: 'Email ✓',
                emailFail: 'Email ✗',
            },
        },
    },
} as const satisfies Record<string, unknown>;

export default en;

// Source-of-truth type derived from the English dictionary structure.
// Object keys stay as string literals (so dot-paths can be derived for
// useT()), but values are widened to plain `string` so cs/sk dictionaries
// can carry their own translations under the same shape.
type WidenLeaves<T> = T extends string
    ? string
    : T extends readonly (infer U)[]
        ? WidenLeaves<U>[]
        : { -readonly [K in keyof T]: WidenLeaves<T[K]> };

export type AppMessages = WidenLeaves<typeof en>;
